import { useEffect, useRef, useState } from 'react';
import { formatISODuration, intervalToDuration } from 'date-fns';
import { duration as durationUtil } from '@eti/utils';

type TimeFormat = {
  hour?: string;
  minute?: string;
  second?: string;
};

export const countdownFactory = (
  languageCode: string,
  timeFormat: TimeFormat,
  storageKey: string,
  timerDurationMinutes: number,
) => {
  const getTimeLeft = (startTime: number) => {
    const diff = startTime - Math.round(new Date().getTime() / 1000);
    const secondsLeft = diff + timerDurationMinutes * 60;

    return secondsLeft > 0 ? secondsLeft : 0;
  };

  const getInitialTimeLeft = () => {
    const startTime = sessionStorage.getItem(storageKey);
    if (startTime) {
      return getTimeLeft(Number(startTime));
    }

    const currentTime = Math.round(new Date().getTime() / 1000);
    sessionStorage.setItem(storageKey, `${currentTime}`);
    return getTimeLeft(currentTime);
  };

  const resetStartTime = () => {
    sessionStorage.removeItem(storageKey);
  };

  const useCountdown = () => {
    const [timeLeft, setTimeLeft] = useState(getInitialTimeLeft);
    const startTimeStamp = Number(sessionStorage.getItem(storageKey));
    const intervalRef = useRef<number>();
    const isExpired = timeLeft < 1;

    useEffect(() => {
      if (!isExpired) {
        intervalRef.current = window.setInterval(() => {
          setTimeLeft(() => {
            return getTimeLeft(startTimeStamp);
          });
        }, 1000);
      }

      return () => {
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
        }
      };
    }, [isExpired, startTimeStamp, timeLeft]);

    const formattedTimeLeft = durationUtil.getDuration(
      timeLeft * 1000,
      languageCode,
      timeFormat,
      ['m', 's'],
      true,
      ', ',
    );

    const isoTimeLeft = formatISODuration(
      intervalToDuration({
        start: startTimeStamp,
        end: startTimeStamp + timeLeft * 1000,
      }),
    );

    return {
      isExpired,
      formattedTimeLeft,
      isoTimeLeft,
      resetStartTime,
    };
  };

  return { useCountdown };
};
